/**
 * Tool Configuration Registry
 *
 * Central registry for all tool display configurations.
 * Import configs from category files and register them here.
 *
 * Phase 3 of the agent tool catalog plan: the *data* half of each config
 * (displayName, icon, loadingText, emptyText, category) now also comes
 * from the AgentToolCatalogService at runtime. Function-typed fields
 * (formatSuccess, isValidOutput, ...) stay in the baked-in configs since
 * proto can't transport functions. `getToolConfig()` merges the two:
 * remote data overrides baked-in display fields when the remote catalog
 * is enabled (REACT_APP_USE_REMOTE_CATALOG=1) and has loaded.
 */ // Import category configs
import { classifierToolConfigs } from './classifier';
import { corporateToolConfigs } from './corporate';
import { e2bToolConfigs } from './e2b';
import { fileToolConfigs } from './file';
import { govconToolConfigs } from './govcon';
import { gsaToolConfigs } from './gsa';
import { getRemoteToolConfig } from './remote';
import { webSearchToolConfigs } from './web-search';
/**
 * All registered tool configurations
 */ const ALL_TOOL_CONFIGS = [
    ...fileToolConfigs,
    ...govconToolConfigs,
    ...gsaToolConfigs,
    ...corporateToolConfigs,
    ...classifierToolConfigs,
    ...e2bToolConfigs,
    ...webSearchToolConfigs
];
/**
 * Tool config registry indexed by tool ID
 */ const TOOL_CONFIG_BY_ID = new Map();
/**
 * Tool config registry indexed by alias
 */ const TOOL_CONFIG_BY_ALIAS = new Map();
// Build the registries
for (const config of ALL_TOOL_CONFIGS){
    TOOL_CONFIG_BY_ID.set(config.id, config);
    // Also register aliases
    if (config.aliases) {
        for (const alias of config.aliases){
            TOOL_CONFIG_BY_ALIAS.set(alias, config);
        }
    }
}
/**
 * Apply remote catalog overrides on top of a baked-in config. Only the
 * data-half fields can be overridden — function-typed formatters stay
 * in the baked-in config since the catalog can't carry functions.
 *
 * Returns the same reference when the remote catalog has nothing to
 * say so consumers can keep their referential-equality assumptions.
 */ function applyRemoteOverrides(base, remote) {
    if (!remote) return base;
    return {
        ...base,
        displayName: remote.displayName ?? base.displayName,
        icon: remote.icon ?? base.icon,
        loadingText: remote.loadingText ?? base.loadingText,
        emptyText: remote.emptyText ?? base.emptyText
    };
}
/**
 * Get tool configuration by ID or alias.
 *
 * Resolves in this order:
 *   1. Baked-in config (TOOL_CONFIG_BY_ID / TOOL_CONFIG_BY_ALIAS / normalized variants).
 *   2. Remote catalog override (if enabled and present) is merged into the baked config.
 *
 * When only the remote catalog knows about a tool (no baked-in config),
 * `undefined` is returned so callers fall through to the generic renderer.
 * Phase 6 will introduce a synthetic baked-in shim so generic tools also
 * pick up remote display data.
 *
 * @param toolId - The tool ID or alias to look up
 * @returns The tool configuration, or undefined if not found
 */ export function getToolConfig(toolId) {
    // Try exact ID match first
    const byId = TOOL_CONFIG_BY_ID.get(toolId);
    if (byId) return applyRemoteOverrides(byId, getRemoteToolConfig(byId.id));
    // Try alias match
    const byAlias = TOOL_CONFIG_BY_ALIAS.get(toolId);
    if (byAlias) return applyRemoteOverrides(byAlias, getRemoteToolConfig(byAlias.id));
    // Try normalized versions (camelCase, kebab-case, snake_case)
    const normalized = normalizeToolId(toolId);
    for (const variant of normalized){
        const config = TOOL_CONFIG_BY_ID.get(variant) || TOOL_CONFIG_BY_ALIAS.get(variant);
        if (config) return applyRemoteOverrides(config, getRemoteToolConfig(config.id));
    }
    return undefined;
}
/**
 * Get all tool configurations
 */ export function getAllToolConfigs() {
    return ALL_TOOL_CONFIGS;
}
/**
 * Get tool configurations by category
 */ export function getToolConfigsByCategory(category) {
    return ALL_TOOL_CONFIGS.filter((config)=>config.category === category);
}
/**
 * Check if a tool has a configuration
 */ export function hasToolConfig(toolId) {
    return getToolConfig(toolId) !== undefined;
}
/**
 * Normalize a tool ID to try different naming conventions
 */ function normalizeToolId(toolId) {
    const variants = [];
    // Remove common prefixes/suffixes
    const withoutTool = toolId.replace(/Tool$/, '').replace(/^tool-?/, '');
    variants.push(withoutTool);
    // Convert camelCase to kebab-case
    const kebab = toolId.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    variants.push(kebab);
    // Convert kebab-case to camelCase
    const camel = toolId.replace(/-([a-z])/g, (_, c)=>c.toUpperCase());
    variants.push(camel);
    // Convert snake_case to kebab-case
    const fromSnake = toolId.replace(/_/g, '-');
    variants.push(fromSnake);
    // Convert kebab-case to snake_case
    const toSnake = toolId.replace(/-/g, '_');
    variants.push(toSnake);
    return [
        ...new Set(variants)
    ];
}
export { CATEGORY_METADATA, classifyToolResult, defaultErrorFormatter, defineToolConfig, formatCount, formatDuration, humanizeToolId } from '../tool-display-config';
